import {SitePreviewStatus} from '../vocabulary.js';

/** How long to wait between two checks of a preview that is being built, in milliseconds. */
export const POLL_INTERVAL_MS = 3000;

/** How long a preview may take before the bar says it is slow and stops checking, in milliseconds. */
export const POLL_LIMIT_MS = 180000;

/** How long a preview usually takes to build, in milliseconds; the progress bar reaches 90% then. */
export const EXPECTED_BUILD_MS = 30000;

/**
 * The estimated progress of a preview build, in whole percent. The backend only says "building",
 * so this is a guess from the elapsed time: linear to 90% at {@link EXPECTED_BUILD_MS}, then
 * creeping towards 99% without ever reaching it, so a slow build never reads as done.
 * @param {number} elapsedMs - time since the preview was requested
 * @returns {number}
 */
export function estimatedProgress(elapsedMs) {
    const elapsed = Math.max(0, elapsedMs);
    if (elapsed <= EXPECTED_BUILD_MS) {
        return Math.floor((90 * elapsed) / EXPECTED_BUILD_MS);
    }
    const overtime = (elapsed - EXPECTED_BUILD_MS) / EXPECTED_BUILD_MS;
    return Math.min(99, Math.floor(90 + 10 * (1 - Math.exp(-overtime))));
}

/**
 * What the site preview bar shows (UI-only): nothing yet, a preview being built, one taking longer
 * than normal, one ready to open, one that failed, or a request that failed.
 * @readonly
 * @enum {string}
 */
export const SitePreviewView = Object.freeze({
    IDLE: 'idle',
    BUILDING: 'building',
    SLOW: 'slow',
    READY: 'ready',
    FAILED: 'failed',
    ERROR: 'error',
});

/**
 * @typedef {Object} SitePreviewState
 * @property {string} kind - one of {@link SitePreviewView}
 * @property {string|null} [url] - the address of a ready preview
 * @property {string} [message] - the text of a failed request
 */

/**
 * The view of a preview status answered by the backend. A ready preview is only shown with an
 * https address; anything else reads as failed. The checks name the commit the build answered,
 * which exists as soon as the build answers, so no preview means there is none to show.
 * @param {{status: string, url?: string|null}} response
 * @param {number} elapsedMs - time since the preview was requested
 * @returns {SitePreviewState}
 */
export function viewState(response, elapsedMs) {
    switch (response?.status) {
        case SitePreviewStatus.NONE:
            return {kind: SitePreviewView.IDLE, url: null};
        case SitePreviewStatus.BUILDING:
            return {kind: elapsedMs >= POLL_LIMIT_MS ? SitePreviewView.SLOW : SitePreviewView.BUILDING, url: null};
        case SitePreviewStatus.READY:
            return isHttpsUrl(response.url)
                ? {kind: SitePreviewView.READY, url: response.url}
                : {kind: SitePreviewView.FAILED, url: null};
        default:
            return {kind: SitePreviewView.FAILED, url: null};
    }
}

/** @param {unknown} value */
function isHttpsUrl(value) {
    try {
        return new URL(String(value)).protocol === 'https:';
    } catch {
        return false;
    }
}
